import React, { useState, useEffect, useRef } from 'react';
import { Send, Plane, MapPin, Calendar, Users, DollarSign, Sparkles } from 'lucide-react';
// Meridian Global Travel Planning Bot - v1.0

const TravelPlannerBot = () => {
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [formData, setFormData] = useState({});
  const [step, setStep] = useState('chat');
  const [isGenerating, setIsGenerating] = useState(false);
  const [finalOutput, setFinalOutput] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const messagesEndRef = useRef(null);
  const hasInitialized = useRef(false);

// Restore saved progress on load
useEffect(() => {
  const savedProgress = localStorage.getItem('meridianTravelProgress');
  if (savedProgress && !hasInitialized.current) {
    try {
      const progressData = JSON.parse(savedProgress);
      const savedDate = new Date(progressData.timestamp);
      const daysSince = (new Date() - savedDate) / (1000 * 60 * 60 * 24);
      
      // Only restore if less than 7 days old
      if (daysSince < 7) {
        setFormData(progressData.formData);
        setCurrentQuestionIndex(progressData.currentQuestionIndex);
        setMessages(progressData.messages);
        hasInitialized.current = true;
      }
    } catch (e) {
      console.error('Error restoring progress:', e);
    }
  }
}, []);

  const questions = [
    { id: 'name', text: "Welcome to Meridian Global Travel! I'm thrilled to help plan your dream vacation. Let's start with your name - what should I call you?", type: 'text', icon: <Users className="w-5 h-5" /> },
    { id: 'phone', text: "Perfect! What's the best phone number to reach you at?", type: 'text', icon: <MapPin className="w-5 h-5" /> },
    { id: 'email', text: "And your email address so we can send you your personalized itinerary?", type: 'email', icon: <MapPin className="w-5 h-5" /> },
    { id: 'whoTraveling', text: "Who will be traveling?", type: 'choice', options: ['Solo trip', 'Friends', 'Couple', 'Family w/ children'], icon: <Users className="w-5 h-5" /> },
    { id: 'numTravelers', text: "How many travelers total (including yourself)?", type: 'text', icon: <Users className="w-5 h-5" /> },
    { id: 'destination', text: "Where are you dreaming of going? (City, country, or region)", type: 'text', icon: <MapPin className="w-5 h-5" /> },
    { id: 'travelDates', text: "When would you like to travel? (Exact dates or month/year)", type: 'text', icon: <Calendar className="w-5 h-5" /> },
    { id: 'dateFlexibility', text: "How flexible are your travel dates?", type: 'choice', options: ['Very flexible', 'Somewhat flexible', 'No flexibility'], icon: <Calendar className="w-5 h-5" /> },
    { id: 'totalDays', text: "How long is your trip? (e.g., '7 days / 6 nights')", type: 'text', icon: <Calendar className="w-5 h-5" /> },
    { id: 'departureAirport', text: "What airport will you depart from?", type: 'text', icon: <Plane className="w-5 h-5" /> }, 
    { id: 'budget', text: "What's your budget for this trip? (Total or per person)", type: 'text', icon: <DollarSign className="w-5 h-5" /> },
    { id: 'tripStyle', text: "How would you describe your ideal trip style? (Select all that apply)", type: 'multi', options: ['Relaxation & spa', 'Adventure & activities', 'Cultural immersion', 'Luxury & indulgence', 'Romantic', 'Family-friendly'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'accommodationType', text: "What is your preferred accommodation type (Hotel, All-Inclusive, AirBnb)?", type: 'text', icon: <MapPin className="w-5 h-5" /> },  
    { id: 'accommodationSize', text: "What size accommodation do you need?", type: 'choice', options: ['1 room', '2 rooms', '3+ rooms', 'Suite', 'Villa/house'], icon: <MapPin className="w-5 h-5" /> },
    { id: 'mustHaves', text: "What are your accommodation must-haves? (Pool, ocean view, central location, etc.)", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'travelExperience', text: "How experienced are you with travel?", type: 'choice', options: ['First major trip', 'Some international experience', 'Frequent traveler', 'Very experienced'], icon: <Plane className="w-5 h-5" /> },
    { id: 'travelPace', text: "What pace do you prefer while traveling?", type: 'multi', options: ['Relaxed', 'Moderate', 'Packed with activities', 'Spontaneous'], icon: <Plane className="w-5 h-5" /> },
    { id: 'activityLevel', text: "How would you rate your preferred activity level?", type: 'multi', options: ['Low (minimal walking)', 'Medium (3-4 hours walking)', 'High (Can go all day)'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'comments', text: "Any special occasions or additional context? (Anniversary, birthday, honeymoon, etc.)", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'waterActivities', text: "Interested in any water activities?", type: 'multi', options: ['Swimming', 'Snorkeling', 'Scuba diving', 'Surfing', 'Boating', 'None'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'adventureActivities', text: "How about adventure activities?", type: 'multi', options: ['Hiking', 'Zip-lining', 'Rock climbing', 'Safari', 'Hot air balloon', 'Theme parks', 'None'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'culturalActivities', text: "Which cultural experiences interest you?", type: 'multi', options: ['Museums', 'Historical sites', 'Local markets', 'Cooking classes', 'Language learning', 'Food tours', 'None'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'foodActivities', text: "What food & dining experiences appeal to you?", type: 'multi', options: ['Fine dining', 'Street food', 'Wine tasting', 'Brewery tours', 'Cooking classes', 'Farm-to-table', 'None'], icon: <Sparkles className="w-5 h-5" /> },
    { id: 'dietaryRestrictions', text: "Do you have any dietary restrictions or preferences?", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'healthConcerns', text: "Any health or mobility considerations we should accommodate?", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'assistanceNeeded', text: "Will you need any special assistance during your trip?", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'thingsToAvoid', text: "Anything you'd like to avoid? (Crowds, certain foods, activities, etc.)", type: 'text', icon: <Sparkles className="w-5 h-5" /> },
    { id: 'otherDetails', text: "Anything else you'd like me to know to create your perfect itinerary? Are there any specific sites, activities, or other experiences you'd like included?",  type: 'text', icon: <Sparkles className="w-5 h-5" /> }
  ];

  useEffect(() => {
    if (messages.length === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      setTimeout(() => addMessage(questions[0].text, 'bot'), 500);
    }
  }, []); // Empty dependency array means this only runs once

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const addMessage = (text, sender) => {
    setMessages(prev => [...prev, { text, sender, timestamp: new Date() }]);
  };

  const handleSend = () => {
    if (!userInput.trim()) {
  // Allow blank for "additional" questions
  const currentQuestion = questions[currentQuestionIndex];
  if (currentQuestion.id.startsWith('additional')) {
    // Continue with empty value
    setFormData(prev => ({ ...prev, [currentQuestion.id]: 'None specified' }));
    setUserInput('');
    
    if (isEditMode) {
      setTimeout(() => {
        setIsEditMode(false);
        setStep('review');
        addMessage("Answer updated! Would you like to edit anything else, or are you ready to proceed?", 'bot');
        setTimeout(() => generateSummary(), 500);
      }, 800);
    } else if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        addMessage(questions[currentQuestionIndex + 1].text, 'bot');
      }, 800);
    } else {
      setTimeout(() => {
        setStep('review');
        generateSummary();
      }, 800);
    }
    return;
  }
  return;
}
    const currentQuestion = questions[currentQuestionIndex];
    addMessage(userInput, 'user');

    if (currentQuestion.type === 'multi') {
      setFormData(prev => ({ ...prev, [currentQuestion.id]: userInput }));
    } else {
      setFormData(prev => ({ ...prev, [currentQuestion.id]: userInput }));
    }

    setUserInput('');

    // If in edit mode, return to review after updating
    if (isEditMode) {
      setTimeout(() => {
        setIsEditMode(false);
        setStep('review');
        addMessage("Answer updated! Would you like to edit anything else, or are you ready to proceed?", 'bot');
        setTimeout(() => generateSummary(), 500);
      }, 800);
    } else if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        addMessage(questions[currentQuestionIndex + 1].text, 'bot');
      }, 800);
    } else {
      setTimeout(() => {
        setStep('review');
        generateSummary();
      }, 800);
    }
  };

const handleSaveProgress = () => {
  const progressData = {
    formData: formData,
    currentQuestionIndex: currentQuestionIndex,
    messages: messages,
    timestamp: new Date().toISOString()
  };
  localStorage.setItem('meridianTravelProgress', JSON.stringify(progressData));
  addMessage("✅ Progress saved! You can close this page and return anytime to continue from where you left off.", 'bot');
};

  const handleOptionClick = (option) => {
    const currentQuestion = questions[currentQuestionIndex];

    if (currentQuestion.type === 'multi') {
      const currentSelections = formData[currentQuestion.id]?.split(', ') || [];
      let newSelections;

      if (option === 'None') {
        newSelections = ['None'];
      } else {
        newSelections = currentSelections.includes(option)
          ? currentSelections.filter(item => item !== option && item !== 'None')
          : [...currentSelections.filter(item => item !== 'None'), option];

        if (newSelections.length === 0) newSelections = ['None'];
      }

      const selectionText = newSelections.join(', ');
      setFormData(prev => ({ ...prev, [currentQuestion.id]: selectionText }));
      // Don't add message or advance - let them select multiple
    } else {
      setUserInput(option);
      setFormData(prev => ({ ...prev, [currentQuestion.id]: option }));
      addMessage(option, 'user');
      setUserInput('');

      // If in edit mode, return to review after updating
      if (isEditMode) {
        setTimeout(() => {
          setIsEditMode(false);
          setStep('review');
          addMessage("Answer updated! Would you like to edit anything else, or are you ready to proceed?", 'bot');
          setTimeout(() => generateSummary(), 500);
        }, 800);
      } else if (currentQuestionIndex < questions.length - 1) {
        setTimeout(() => {
          setCurrentQuestionIndex(prev => prev + 1);
          addMessage(questions[currentQuestionIndex + 1].text, 'bot');
        }, 800);
      } else {
        setTimeout(() => {
          setStep('review');
          generateSummary();
        }, 800);
      }
    }
  };

  const handleMultiContinue = () => {
    const currentQuestion = questions[currentQuestionIndex];
    const selections = formData[currentQuestion.id] || 'None';
    
    addMessage(selections, 'user');

    // If in edit mode, return to review
    if (isEditMode) {
      setTimeout(() => {
        setIsEditMode(false);
        setStep('review');
        addMessage("Answer updated! Would you like to edit anything else, or are you ready to proceed?", 'bot');
        setTimeout(() => generateSummary(), 500);
      }, 800);
    } else if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestionIndex(prev => prev + 1);
        addMessage(questions[currentQuestionIndex + 1].text, 'bot');
      }, 800);
    } else {
      setTimeout(() => {
        setStep('review');
        generateSummary();
      }, 800);
    }
  };

  const generateSummary = () => {
    let summary = "Review Your Travel Details:\n\n";
    
    Object.keys(formData).forEach(key => {
      const question = questions.find(q => q.id === key);
      if (question) {
        summary += question.text + "\n";
        summary += "→ " + formData[key] + "\n\n";
      }
    });
    
    summary += "\nType 'yes' to confirm and receive your personalized itinerary, or click 'Edit Answers' below to make changes.";
    
    addMessage(summary, 'bot');
  };


    const handleConfirmKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleConfirmClick();
  }
};
  
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
    }
  };  
    const handleConfirmClick = () => {
      if (userInput.toLowerCase() === 'yes') {
        generateFinalOutput();
      } else if (userInput.toLowerCase() === 'edit') {
        addMessage("No problem! Let's start over.", 'bot');
        setTimeout(() => window.location.reload(), 1000);
      } else {
        addMessage('Please type "yes" to confirm or "edit" to make changes.', 'bot');
      }
      setUserInput('');
  };

  const generateFinalOutput = async () => {
    setIsGenerating(true);
    setStep('generating');

    const dataWithTimestamp = {
      ...formData,
      timestamp: new Date().toISOString(),
      emailAddress: formData.email
    };

    try {
      const webhookUrl = "https://meridianglobaltravel.app.n8n.cloud/webhook/travel-intake";

      console.log('Sending data to n8n workflow...');

      const webhookResponse = await fetch(webhookUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataWithTimestamp)
      });

      if (!webhookResponse.ok) {
        throw new Error("Workflow failed: " + webhookResponse.status + " " + webhookResponse.statusText);
}
      const webhookResult = await webhookResponse.json();
      console.log('Workflow triggered successfully:', webhookResult);

      const successMessage = "Trip Intake Complete!\n\nThank you, " + formData.name + "! We've received your travel preferences for " + formData.destination + ".\n\nOur team is now:\n- Researching your destination\n- Creating a personalized itinerary\n- Generating a beautiful travel plan\n\nYou'll receive an email shortly with your custom itinerary. We can't wait to help plan your perfect trip!";

      setFinalOutput(successMessage);
      setStep('complete');
      addMessage(successMessage, 'bot');

    } catch (error) {
      console.error('Error submitting to workflow:', error);

       const errorMessage = "I apologize, but I encountered an error submitting your information.\n\nThis could be due to:\n- Network connectivity issues\n- Workflow not activated in n8n\n- Incorrect webhook URL\n\nPlease try again in a moment, or contact us directly:\nEmail: meridiantravelplan@gmail.com\n\nDon't worry - your information is safe and we'd love to help you plan your trip!";

      addMessage(errorMessage, 'bot');
      setStep('error');
    } finally {
      setIsGenerating(false);
    }
  };

   
  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f1419 0%, #1a2332 50%, #243447 100%)',
      padding: '20px',
      fontFamily: '"Playfair Display", Georgia, serif'
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto 30px',
        textAlign: 'center',
        animation: 'fadeIn 1s ease-in'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '15px',
          marginBottom: '10px'
        }}>
          <img 
            src="https://i.imgur.com/yAZgVGf.jpeg" 
            alt="Meridian Global Travel Logo"
            style={{ 
              width: '80px', 
              height: '80px',
              filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))'
            }}
            onError={(e) => {
              // Fallback if logo doesn't load - show globe icon
              e.target.style.display = 'none';
            }}
          />
          <h1 style={{
            color: '#d4af37',
            fontSize: '48px',
            fontWeight: '700',
            margin: '0',
            letterSpacing: '1px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
          }}>
            Meridian Global Travel
          </h1>
        </div>
        <p style={{
          color: 'rgba(255,255,255,0.95)',
          fontSize: '18px',
          margin: '0',
          fontFamily: '"Lato", sans-serif',
          fontWeight: '300',
          letterSpacing: '2px'
        }}>
          CRAFTING EXTRAORDINARY JOURNEYS
        </p>
      </div>

      {/* Chat Container */}
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        backgroundColor: 'white',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
        animation: 'slideUp 0.6s ease-out'
      }}>
        {/* Messages */}
        <div style={{
          height: '600px',
          overflowY: 'auto',
          padding: '30px',
          background: 'linear-gradient(to bottom, #fafafa 0%, #ffffff 100%)'
        }}>
          {messages.map((msg, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: '20px',
                animation: "fadeIn 0.5s ease-in " + (index * 0.1) + "s both"
              }}
            >
              <div style={{
                maxWidth: '75%',
                padding: '18px 24px',
                borderRadius: msg.sender === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                backgroundColor: msg.sender === 'user' ? '#d4af37' : '#f3f4f6',
                color: msg.sender === 'user' ? 'white' : '#1f2937',
                boxShadow: msg.sender === 'user' 
                  ? '0 4px 12px rgba(212, 175, 55, 0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.08)',
                fontFamily: '"Lato", sans-serif',
                fontSize: '16px',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap'
              }}>
                {msg.text}
              </div>
            </div>
          ))}

          {/* Choice Buttons */}
          {step === 'chat' && currentQuestion.type === 'choice' && (
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginTop: '15px',
              animation: 'fadeIn 0.5s ease-in'
            }}>
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionClick(option)}
                  style={{
                    padding: '14px 28px',
                    backgroundColor: 'white',
                    color: '#2d5f3f',
                    border: '2px solid #2d5f3f',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontFamily: '"Lato", sans-serif',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(13, 148, 136, 0.15)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#2d5f3f';
                    e.target.style.color = 'white';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(45, 95, 63, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.color = '#2d5f3f';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(45, 95, 63, 0.15)';
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Multi-select Buttons */}
          {step === 'chat' && currentQuestion.type === 'multi' && (
            <>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                marginTop: '15px',
                animation: 'fadeIn 0.5s ease-in'
              }}>
                {currentQuestion.options.map((option, index) => {
                  const isSelected = formData[currentQuestion.id]?.includes(option);
                  return (
                    <button
                      key={index}
                      onClick={() => handleOptionClick(option)}
                      style={{
                        padding: '14px 28px',
                        backgroundColor: isSelected ? '#d4af37' : 'white',
                        color: isSelected ? 'white' : '#2d5f3f',
                        border: "2px solid " + (isSelected ? '#d4af37' : '#2d5f3f'),
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontFamily: '"Lato", sans-serif',
                        fontWeight: '500',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected 
                          ? '0 4px 12px rgba(212, 175, 55, 0.3)' 
                          : '0 2px 8px rgba(45, 95, 63, 0.15)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = '#2d5f3f';
                          e.target.style.color = 'white';
                        }
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.target.style.backgroundColor = 'white';
                          e.target.style.color = '#2d5f3f';
                        }
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      {isSelected && '✓ '}{option}
                    </button>
                  );
                })}
              </div>
              {/* Continue button for multi-select */}
              <div style={{
                marginTop: '20px',
                textAlign: 'center',
                animation: 'fadeIn 0.5s ease-in 0.3s both'
              }}>
                <button
                  onClick={handleMultiContinue}
                  disabled={!formData[currentQuestion.id]}
                  style={{
                    padding: '16px 40px',
                    backgroundColor: formData[currentQuestion.id] ? '#d4af37' : '#cbd5e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: formData[currentQuestion.id] ? 'pointer' : 'not-allowed',
                    fontSize: '16px',
                    fontWeight: '600',
                    fontFamily: '"Lato", sans-serif',
                    transition: 'all 0.3s ease',
                    boxShadow: formData[currentQuestion.id] 
                      ? '0 4px 12px rgba(212, 175, 55, 0.3)' 
                      : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (formData[currentQuestion.id]) {
                      e.target.style.backgroundColor = '#b8941f';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(212, 175, 55, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (formData[currentQuestion.id]) {
                      e.target.style.backgroundColor = '#d4af37';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
                    }
                  }}
                >
                  Continue →
                </button>
              </div>
            </>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        {(step === 'chat' || step === 'review') && (
          <div style={{
            padding: '25px 30px',
            backgroundColor: '#f9fafb',
            borderTop: '1px solid #e5e7eb'
          }}>
            {step === 'review' && (
              <div style={{
                marginBottom: '20px',
                padding: '20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '2px solid #e5e7eb'
              }}>
                <p style={{
                  margin: '0 0 15px 0',
                  fontFamily: '"Lato", sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#2d5f3f'
                }}>
                  Want to change an answer? Select a question:
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '10px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  padding: '5px'
                }}>
                  {questions.map((q, index) => (
                    <button
                      key={q.id}
                      onClick={() => {
                        setCurrentQuestionIndex(index);
                        setIsEditMode(true);
                        setStep('chat');
                        addMessage("Let's update: " + q.text, 'bot');
                      }}
                      style={{
                        padding: '10px 15px',
                        backgroundColor: formData[q.id] ? '#f0fdf4' : '#fee2e2',
                        color: '#2d5f3f',
                        border: '1px solid #d1fae5',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: '"Lato", sans-serif',
                        textAlign: 'left',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#dcfce7';
                        e.target.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = formData[q.id] ? '#f0fdf4' : '#fee2e2';
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      {formData[q.id] ? '✓ ' : '○ '}
                      Q{index + 1}: {q.id}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap'
}}>
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={step === 'review' ? handleConfirmKeyPress : handleKeyPress}
                placeholder={step === 'review' ? 'Type "yes" to confirm...' : 'Type your answer...'}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '16px 20px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontFamily: '"Lato", sans-serif',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2d5f3f';
                  e.target.style.boxShadow = '0 0 0 3px rgba(45, 95, 63, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={step === 'review' ? handleConfirmClick : handleSend}
                disabled={isGenerating}
                style={{
                  padding: '16px 24px',
                  backgroundColor: isGenerating ? '#94a3b8' : '#d4af37',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: '600',
                  fontFamily: '"Lato", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isGenerating) {
                    e.target.style.backgroundColor = '#b8941f';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(212, 175, 55, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isGenerating) {
                    e.target.style.backgroundColor = '#d4af37';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
                  }
                }}
              >
                <Send className="w-5 h-5" />
                {step === 'review' ? 'Confirm' : 'Send'}
              </button>
{step === 'chat' && currentQuestionIndex > 0 && (
  <button
    onClick={handleSaveProgress}
    style={{
      padding: '16px 24px',
      backgroundColor: '#2d5f3f',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: '"Lato", sans-serif',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(45, 95, 63, 0.3)'
    }}
    onMouseEnter={(e) => {
      e.target.style.backgroundColor = '#1f4e2e';
      e.target.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.target.style.backgroundColor = '#2d5f3f';
      e.target.style.transform = 'translateY(0)';
    }}
 >
    💾 Save Progress
  </button>
)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TravelPlannerBot;